import React, { useCallback, useEffect, useState } from "react";
import { executeMutation, executeQuery } from "../../../graphql/client";

// Seasons tab (epic #307 phase 2): create/edit the season container
// (name, sport, window, status, notes), pick which nights are in it
// from the events dated inside the window, and read the live
// standings with a copyable shout-out for the main chat / outbox.
// Standings themselves are computed from the compete log — this tab
// never writes scores.

const OCEAN = "var(--ab-ocean, #0e7c9b)";
const SEAFOAM = "var(--ab-seafoam, #e7f4f3)";

const SEASON_FIELDS = `seasonId name sport startDate endDate status eventIds notes createdAt updatedAt`;

const LIST_QUERY = `query AdminSeasons { seasons(includeArchived: true) { ${SEASON_FIELDS} } }`;

const DETAIL_QUERY = `query AdminSeasonDetail($seasonId: ID!) {
  seasonStandings(seasonId: $seasonId) {
    nightsTotal nightsPlayed playersCount gamesCounted leaderName shoutout
    standings { rank userSub name nightsPlayed gameWins gameLosses boardWins }
    nights { socialEventId title date rsvpYes approvedPlayers gamesCounted gamesPending gamesDisputed leaderName leaderBoardWins }
    monthlyChampions { month label name boardWins runnerUpName runnerUpBoardWins players }
  }
  seasonEventCandidates(seasonId: $seasonId) { socialEventId title date inSeason }
}`;

const UPSERT = `mutation UpsertSeason($input: SeasonInput!) {
  upsertSeason(input: $input) { ${SEASON_FIELDS} }
}`;

const STATUSES = ["upcoming", "active", "ended", "archived"];

const card = {
  border: "1px solid #e6e9f0",
  borderRadius: 10,
  background: "#fff",
  padding: "12px 14px",
  marginBottom: 14,
};
const muted = { color: "#6a7385", fontSize: 13 };
const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#6a7385",
  fontWeight: 600,
  padding: "4px 6px",
  borderBottom: "1px solid #e6e9f0",
  whiteSpace: "nowrap",
};
const td = { padding: "6px", fontSize: 14, verticalAlign: "top" };
const num = { ...td, textAlign: "right", whiteSpace: "nowrap" };
const input = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid #d0d6e0",
  borderRadius: 6,
  fontSize: 14,
  boxSizing: "border-box",
};
const label = { fontSize: 12, color: "#6a7385", fontWeight: 600, marginBottom: 4 };
const btn = (primary) => ({
  padding: "8px 14px",
  background: primary ? OCEAN : "#fff",
  color: primary ? "#fff" : "#333",
  border: primary ? "none" : "1px solid #d7dbe4",
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  marginRight: 8,
});
const pill = (active) => ({
  border: active ? `2px solid ${OCEAN}` : "1px solid #d7dbe4",
  background: active ? SEAFOAM : "#fff",
  color: "#333",
  borderRadius: 999,
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  cursor: "pointer",
  marginRight: 8,
  marginBottom: 8,
});

const Stat = ({ label: l, value, sub }) => (
  <div style={{ ...card, marginBottom: 0, flex: "1 1 120px", padding: "10px 12px" }}>
    <div style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>{l}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: "#1f2330" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#8a93a3" }}>{sub}</div>}
  </div>
);

const emptyForm = () => ({
  seasonId: "",
  name: "",
  sport: "Pickleball",
  startDate: "",
  endDate: "",
  status: "upcoming",
  notes: "",
  eventIds: [],
});

const toForm = (s) => ({
  seasonId: s.seasonId,
  name: s.name || "",
  sport: s.sport || "",
  startDate: s.startDate || "",
  endDate: s.endDate || "",
  status: s.status || "active",
  notes: s.notes || "",
  eventIds: s.eventIds || [],
});

const SeasonsTab = () => {
  const [seasons, setSeasons] = useState([]);
  const [selected, setSelected] = useState(null); // seasonId or "new"
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadList = useCallback(async () => {
    const data = await executeQuery(LIST_QUERY);
    const list = data.seasons || [];
    setSeasons(list);
    return list;
  }, []);

  const loadDetail = useCallback(async (seasonId) => {
    const data = await executeQuery(DETAIL_QUERY, { seasonId });
    setDetail(data.seasonStandings);
    setCandidates(data.seasonEventCandidates || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const list = await loadList();
        const first = list.find((s) => s.status !== "archived") || list[0];
        if (first) {
          setSelected(first.seasonId);
          setForm(toForm(first));
          await loadDetail(first.seasonId);
        } else {
          setSelected("new");
        }
        setError("");
      } catch (err) {
        setError(err.message || "Could not load seasons");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadList, loadDetail]);

  const pick = async (seasonId) => {
    setNotice("");
    setError("");
    if (seasonId === "new") {
      setSelected("new");
      setForm(emptyForm());
      setDetail(null);
      setCandidates([]);
      return;
    }
    const s = seasons.find((x) => x.seasonId === seasonId);
    if (!s) return;
    setSelected(seasonId);
    setForm(toForm(s));
    setDetail(null);
    try {
      await loadDetail(seasonId);
    } catch (err) {
      setError(err.message || "Could not load that season");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleEvent = (socialEventId) =>
    setForm((f) => ({
      ...f,
      eventIds: f.eventIds.includes(socialEventId)
        ? f.eventIds.filter((id) => id !== socialEventId)
        : [...f.eventIds, socialEventId],
    }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        name: form.name,
        sport: form.sport || null,
        startDate: form.startDate,
        endDate: form.endDate,
        status: form.status,
        notes: form.notes || null,
        eventIds: form.eventIds,
      };
      if (selected !== "new" && form.seasonId) payload.seasonId = form.seasonId;
      const data = await executeMutation(UPSERT, { input: payload });
      const saved = data.upsertSeason;
      await loadList();
      setSelected(saved.seasonId);
      setForm(toForm(saved));
      await loadDetail(saved.seasonId);
      setNotice(`Saved. Members see it at /seasons/${saved.seasonId}`);
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotice(`${what} copied.`);
    } catch (err) {
      setError("Copy failed — select the text and copy it by hand.");
    }
  };

  const standingsUrl =
    selected && selected !== "new"
      ? `${window.location.origin}/seasons/${selected}`
      : "";

  if (loading) return <p style={muted}>Loading seasons…</p>;

  // Candidates inside the window plus any already-chosen ids that
  // fall outside it (window edited after picking), so nothing picked
  // can silently vanish from the checklist.
  const candidateIds = new Set(candidates.map((c) => c.socialEventId));
  const extraChosen = form.eventIds
    .filter((id) => !candidateIds.has(id))
    .map((id) => ({ socialEventId: id, title: null, date: id.slice(0, 10) }));
  const checklist = [...candidates, ...extraChosen];

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        {seasons.map((s) => (
          <button
            key={s.seasonId}
            type="button"
            style={pill(selected === s.seasonId)}
            onClick={() => pick(s.seasonId)}
          >
            {s.status === "archived" ? "🗄️ " : ""}
            {s.name}
          </button>
        ))}
        <button type="button" style={pill(selected === "new")} onClick={() => pick("new")}>
          + New season
        </button>
      </div>

      {error && (
        <div style={{ ...card, borderColor: "#f3c2c2", color: "#b02a2a" }}>{error}</div>
      )}
      {notice && (
        <div style={{ ...card, background: SEAFOAM, borderColor: "#bfe0dc" }}>{notice}</div>
      )}

      {detail && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <Stat label="Nights" value={`${detail.nightsPlayed}/${detail.nightsTotal}`} sub="played / scheduled" />
            <Stat label="Players" value={detail.playersCount} sub="with counted games" />
            <Stat label="Games" value={detail.gamesCounted} sub="counted" />
            <Stat label="Leader" value={detail.leaderName || "—"} sub={detail.standings[0] ? `${detail.standings[0].boardWins} wins` : "no games yet"} />
          </div>

          <div style={card}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              <strong>Members' link:</strong>
              <code style={{ fontSize: 13 }}>{standingsUrl}</code>
              <button type="button" style={btn(false)} onClick={() => copy(standingsUrl, "Link")}>
                Copy link
              </button>
              {detail.shoutout && (
                <button type="button" style={btn(true)} onClick={() => copy(detail.shoutout, "Shout-out")}>
                  Copy standings shout-out
                </button>
              )}
            </div>
            {detail.shoutout && (
              <pre
                style={{
                  marginTop: 10,
                  marginBottom: 0,
                  whiteSpace: "pre-wrap",
                  fontSize: 13,
                  background: "#f7f8fa",
                  padding: 10,
                  borderRadius: 6,
                }}
              >
                {detail.shoutout}
              </pre>
            )}
            {detail.monthlyChampions.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 14 }}>
                {detail.monthlyChampions.map((m) => (
                  <div key={m.month}>
                    <span role="img" aria-label="trophy">🏆</span>{" "}
                    <strong>{m.label}:</strong> {m.name || "Member"} — {m.boardWins} wins
                    {m.runnerUpName ? ` (runner-up ${m.runnerUpName}, ${m.runnerUpBoardWins})` : ""}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <form onSubmit={save} style={card}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>
          {selected === "new" ? "New season" : "Edit season"}
          {selected !== "new" && form.seasonId && (
            <span style={{ ...muted, marginLeft: 8 }}>id: {form.seasonId}</span>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={label}>Name</div>
            <input style={input} value={form.name} onChange={set("name")} placeholder="Pickleball Season 1 — Fall 2026" required maxLength={80} />
          </div>
          <div>
            <div style={label}>Sport</div>
            <input style={input} value={form.sport} onChange={set("sport")} placeholder="Pickleball" maxLength={40} />
          </div>
          <div>
            <div style={label}>Status</div>
            <select style={input} value={form.status} onChange={set("status")}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={label}>First day</div>
            <input style={input} type="date" value={form.startDate} onChange={set("startDate")} required />
          </div>
          <div>
            <div style={label}>Last day</div>
            <input style={input} type="date" value={form.endDate} onChange={set("endDate")} required />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={label}>Notes (shown to members)</div>
            <input style={input} value={form.notes} onChange={set("notes")} placeholder="Tuesdays 7pm · Fort Family courts · final on Dec 16" maxLength={500} />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={label}>
            Nights in this season ({form.eventIds.length} picked)
          </div>
          {selected === "new" ? (
            <p style={{ ...muted, marginBottom: 0 }}>
              Save the season first; events dated inside its window then
              appear here to pick from.
            </p>
          ) : checklist.length === 0 ? (
            <p style={{ ...muted, marginBottom: 0 }}>
              No events dated between {form.startDate || "?"} and {form.endDate || "?"} yet.
              Create the tourney nights on the calendar first, then come back and tick them.
            </p>
          ) : (
            <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #e6e9f0", borderRadius: 6, padding: 8 }}>
              {checklist.map((c) => (
                <label key={c.socialEventId} style={{ display: "block", fontSize: 14, padding: "3px 0", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={form.eventIds.includes(c.socialEventId)}
                    onChange={() => toggleEvent(c.socialEventId)}
                    style={{ marginRight: 8 }}
                  />
                  {c.title || c.socialEventId}
                  <span style={{ ...muted, marginLeft: 6 }}>{c.date}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          <button type="submit" style={btn(true)} disabled={busy}>
            {busy ? "Saving…" : selected === "new" ? "Create season" : "Save changes"}
          </button>
          <span style={muted}>
            Archived seasons disappear for members. Standings are never stored — they come from the nights' boards.
          </span>
        </div>
      </form>

      {detail && (
        <>
          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Standings</div>
            {detail.standings.length === 0 ? (
              <p style={{ ...muted, marginBottom: 0 }}>
                No counted games yet. Approve RSVPs on each night's board so games count.
              </p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>#</th>
                    <th style={th}>Player</th>
                    <th style={{ ...th, textAlign: "right" }}>Wins</th>
                    <th style={{ ...th, textAlign: "right" }}>W-L</th>
                    <th style={{ ...th, textAlign: "right" }}>Nights</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.standings.map((r) => (
                    <tr key={r.userSub}>
                      <td style={num}>{r.rank}</td>
                      <td style={td}>{r.name || r.userSub}</td>
                      <td style={{ ...num, fontWeight: 700 }}>{r.boardWins}</td>
                      <td style={num}>{r.gameWins}-{r.gameLosses}</td>
                      <td style={num}>{r.nightsPlayed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Nights</div>
            {detail.nights.length === 0 ? (
              <p style={{ ...muted, marginBottom: 0 }}>No nights picked yet.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Night</th>
                    <th style={{ ...th, textAlign: "right" }}>Going</th>
                    <th style={{ ...th, textAlign: "right" }}>Approved</th>
                    <th style={{ ...th, textAlign: "right" }}>Counted</th>
                    <th style={{ ...th, textAlign: "right" }}>Pending</th>
                    <th style={{ ...th, textAlign: "right" }}>Disputed</th>
                    <th style={th}>Night's best</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.nights.map((n) => (
                    <tr key={n.socialEventId}>
                      <td style={td}>
                        <a href={`/compete/${n.socialEventId}`} target="_blank" rel="noopener noreferrer">
                          {n.title || n.socialEventId}
                        </a>
                        <div style={{ fontSize: 12, color: "#8a93a3" }}>{n.date}</div>
                      </td>
                      <td style={num}>{n.rsvpYes}</td>
                      <td style={num}>{n.approvedPlayers}</td>
                      <td style={num}>{n.gamesCounted}</td>
                      <td style={{ ...num, color: n.gamesPending ? "#8a5a00" : undefined }}>{n.gamesPending}</td>
                      <td style={{ ...num, color: n.gamesDisputed ? "#b02a2a" : undefined }}>{n.gamesDisputed}</td>
                      <td style={td}>{n.leaderName ? `${n.leaderName} (${n.leaderBoardWins})` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ ...muted, marginTop: 8 }}>
              Pending = games whose logger's RSVP isn't approved yet; open the night's board to approve.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SeasonsTab;
